/**
 * 后台控制台外壳与路由。
 *
 * 三层守卫，缺一不可：
 *   ① 未登录 → 一律回 /login（员工 token 与成员 token 受众隔离，见 docs/权限模型.md）；
 *   ② 导航项按 `permissions` 显示 —— 权限清单来自接口，前端不硬编码角色；
 *   ③ 页面级 403 仍然照实展示：隐藏入口不等于有权限，服务端才是唯一裁决者。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { adminApi, getToken, setToken, setUnauthorizedHandler } from './lib/api.ts';
import type { StaffSession } from './lib/api.ts';
import { scopeLabel } from './lib/format.ts';
import { LoginPage } from './pages/LoginPage.tsx';
import { OverviewPage } from './pages/OverviewPage.tsx';
import { MembersPage } from './pages/MembersPage.tsx';
import { MemberCreatePage } from './pages/MemberCreatePage.tsx';
import { MemberDetailPage } from './pages/MemberDetailPage.tsx';
import { InvitesPage } from './pages/InvitesPage.tsx';
import { AlertsPage } from './pages/AlertsPage.tsx';
import { ReportsPage } from './pages/ReportsPage.tsx';
import { ReportDetailPage } from './pages/ReportDetailPage.tsx';
import { ResourcesPage } from './pages/ResourcesPage.tsx';
import { ResourceDetailPage } from './pages/ResourceDetailPage.tsx';
import { AnalyticsPage } from './pages/AnalyticsPage.tsx';
import { PermissionsPage } from './pages/PermissionsPage.tsx';

interface NavItem {
  path: string;
  label: string;
  title: string;
  sub: string;
  /** 需要的权限点；缺省表示登录即可见。 */
  permission?: string;
}

/** 导航清单：每一项自己声明所需权限点，App 只做「有 / 没有」的过滤。 */
const NAV_ITEMS: NavItem[] = [
  { path: '/', label: '概览', title: '控制台概览', sub: 'OVERVIEW' },
  { path: '/members', label: '成员与会籍', title: '成员与会籍', sub: 'MEMBERS' },
  // 邀请码管理：成员注册的唯一入口。缺 invite:manage 的角色连入口都看不到；
  // 有入口也不等于能发码 —— 服务端才是裁决者（403 原文在页面上照实显示）。
  { path: '/invites', label: '邀请码管理', title: '邀请码管理', sub: 'INVITES', permission: 'invite:manage' },
  { path: '/alerts', label: '预警处置', title: '预警处置', sub: 'ALERTS' },
  // 报告复核：缺 report:read 的角色（平台、资源审核员等）连入口都看不到；
  // 有入口也不等于有复核权 —— 复核动作另需 report:review，由服务端裁决。
  { path: '/reports', label: '报告复核', title: '报告复核', sub: 'REPORTS', permission: 'report:read' },
  // 资源准入审核：三级准入 + 实地核验 + 持续动态评估 + 清退。缺 resource:approve 的角色
  //（运营 / 健康管家 / 专家 / 财务 / 数据运营 / 中台管理员）连入口都看不到；直接访问 URL 时
  // 页面会用页面级 PermissionBlock 照实说明服务端 403 原文。
  // 资源审核员的 scope 是 none：他能审机构与专家，**看不到成员数据** —— 这不是界面隐藏，
  // 而是服务端在成员接口上直接 403（见 verify-resources.ts 的断言）。
  { path: '/resources', label: '资源准入', title: '资源准入审核', sub: 'RESOURCES', permission: 'resource:approve' },
  // 埋点看板：只给聚合指标，不要求守护关系，因此权限点单独为 analytics:read
  //（数据运营 / 中台管理员具备）。缺该权限点的角色连入口都看不到；直接访问 URL 时
  // 页面会用页面级 PermissionBlock 照实说明服务端 403 原文。
  { path: '/analytics', label: '埋点看板', title: '埋点看板', sub: 'ANALYTICS', permission: 'analytics:read' },
  { path: '/permissions', label: '权限矩阵', title: '权限矩阵', sub: 'PERMISSIONS', permission: 'staff:manage' },
];

/**
 * 不在导航里、但需要有自己的标题的子页面。
 *
 * 顺序即优先级：`/members/new` 必须排在 `/members/` 前面，
 * 否则建档页会被当成「成员详情」而标题写错（前缀匹配的经典坑）。
 */
const SUB_PAGE_TITLES: { prefix: string; title: string; sub: string }[] = [
  { prefix: '/members/new', title: '成员建档', sub: 'NEW MEMBER' },
  { prefix: '/members/', title: '成员详情', sub: 'MEMBER DETAIL' },
  { prefix: '/reports/', title: '报告详情', sub: 'REPORT DETAIL' },
  { prefix: '/resources/', title: '资源准入详情', sub: 'RESOURCE DETAIL' },
];

function titleOf(path: string): { title: string; sub: string } {
  const nav = NAV_ITEMS.find((item) => item.path === path);
  if (nav) return { title: nav.title, sub: nav.sub };
  const sub = SUB_PAGE_TITLES.find((item) => path.startsWith(item.prefix));
  return sub ? { title: sub.title, sub: sub.sub } : { title: '控制台', sub: 'CONSOLE' };
}

function Spinner() {
  return (
    <div className="state-box">
      <div className="spinner" />
      <div>正在校验员工登录状态…</div>
    </div>
  );
}

/** 顶部条：可见范围与守护成员数都来自 `/api/admin/me`，界面不自行推断。 */
function TopBar({ session, path }: { session: StaffSession; path: string }) {
  const meta = titleOf(path);
  return (
    <div className="topbar">
      <div className="tb-title">
        {meta.title}
        <small>{meta.sub}</small>
      </div>
      <div className="tb-meta">
        <span className="badge ghost">可见范围：{scopeLabel(session.scope)}</span>
        <span className="badge h">
          {session.visibleMemberCount === null ? '不限成员数' : `守护成员 ${session.visibleMemberCount} 名`}
        </span>
        <span className="badge e">{session.staff.roleName}</span>
      </div>
    </div>
  );
}

function AdminShell({ session, onLogout }: { session: StaffSession; onLogout: () => void }) {
  const location = useLocation();
  const visibleNav = useMemo(
    () => NAV_ITEMS.filter((item) => item.permission === undefined || session.permissions.includes(item.permission)),
    [session.permissions],
  );

  return (
    <div className="app">
      <aside className="sidebar">
        <div>
          <div className="brand-mark">HEW³</div>
          <div className="brand-sub">中台与后台控制台</div>
          <div className="brand-tag">ROLE × SCOPE · 双维度授权</div>
        </div>

        <nav className="side-nav">
          <div className="side-group">导航</div>
          {visibleNav.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/'}
              className={({ isActive }) => `nav-item${isActive ? ' on' : ''}`}
            >
              <span className="dot" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="side-foot">
          <div className="who-name">{session.staff.name}</div>
          <div className="who-meta">
            {session.staff.roleName} · {session.staff.no}
          </div>
          <div className="who-meta">可见范围：{scopeLabel(session.scope)}</div>
          <div className="sp8" />
          <button type="button" className="btn sm block" onClick={onLogout}>
            退出登录
          </button>
        </div>
      </aside>

      <div className="content">
        <TopBar session={session} path={location.pathname} />
        <div className="body">
          <Routes>
            <Route path="/" element={<OverviewPage />} />
            {/* 建档页必须排在 /members/:id 之前：路由按顺序匹配，反了会把 "new" 当成成员 id */}
            <Route path="/members/new" element={<MemberCreatePage permissions={session.permissions} />} />
            <Route path="/members" element={<MembersPage permissions={session.permissions} />} />
            {/* 成员详情页内含权益核销区块：核销动作是否出现由 permissions 里的 benefit:consume 决定 */}
            <Route path="/members/:id" element={<MemberDetailPage permissions={session.permissions} />} />
            <Route path="/invites" element={<InvitesPage />} />
            <Route path="/alerts" element={<AlertsPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/reports/:id" element={<ReportDetailPage />} />
            {/* 资源准入：列表（含新建）与详情（三级审核推进 + 上架 + 清退处置） */}
            <Route path="/resources" element={<ResourcesPage />} />
            <Route path="/resources/:id" element={<ResourceDetailPage />} />
            <Route path="/analytics" element={<AnalyticsPage />} />
            <Route path="/permissions" element={<PermissionsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  // undefined = 还在校验；null = 未登录
  const [session, setSession] = useState<StaffSession | null | undefined>(undefined);

  useEffect(() => {
    // token 失效（服务端吊销 / 过期）时自动退回登录页
    setUnauthorizedHandler(() => setSession(null));
    void (async () => {
      if (!getToken()) {
        setSession(null);
        return;
      }
      try {
        setSession(await adminApi.me());
      } catch {
        // 401 已在 api 层清掉 token；其余错误同样回登录页，避免停在空壳里
        setSession(null);
      }
    })();
  }, []);

  const handleLoggedIn = useCallback(async (token: string) => {
    setToken(token);
    try {
      setSession(await adminApi.me());
    } catch {
      setToken(null);
      setSession(null);
    }
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await adminApi.logout();
    } catch {
      // 服务端吊销失败也要清本地登录态：不能因为网络问题把人留在后台里
    }
    setToken(null);
    setSession(null);
  }, []);

  if (session === undefined) return <Spinner />;

  return (
    <Routes>
      <Route
        path="/login"
        element={session ? <Navigate to="/" replace /> : <LoginPage onLoggedIn={handleLoggedIn} />}
      />
      <Route
        path="/*"
        element={
          session ? (
            <AdminShell session={session} onLogout={() => void handleLogout()} />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
    </Routes>
  );
}
